import { useMemo, useState } from 'react'
import type { JSX } from 'react'

import {
  Avatar,
  Badge,
  Button,
  CheckIcon,
  Columns3Icon,
  CriticalIcon,
  DropdownMenu,
  EllipsisVerticalIcon,
  EmptyState,
  Flex,
  Heading,
  IconButton,
  LinkIcon,
  ListFilterIcon,
  LockIcon,
  PencilIcon,
  SearchIcon,
  Select,
  SendHorizontalIcon,
  Skeleton,
  Table,
  Text,
  toast,
  TrashIcon,
  UserIcon,
  VisuallyHidden,
  XIcon,
} from '@buffer-mono/popcorn'

import {
  useAccountId,
  useHasOrganizationAction,
  useOrganizationId,
  useOrganizationMembersTotal,
} from '~publish/contexts/AccountContext'

import {
  useAssignableChannels,
  usePendingInvitations,
  useResendInvitation,
  useRevokeInvitation,
} from '../../hooks'
import type { useTeamMembers } from '../../hooks'
import type { PendingInvitation, TeamMember } from '../../hooks'
import {
  COLLABORATION_ROLE_LABELS,
  COLLABORATION_ROLES,
} from '../../utils/collaborationRoles'
import type { CollaborationRole } from '../../utils/collaborationRoles'
import { getDisplayName } from '../../utils/getDisplayName'
import {
  canShowPendingInvitations,
  EMPTY_TEAM_MEMBERS_FILTERS,
  hasActiveTeamMembersFilters,
} from '../../utils/teamMembersFilters'
import type { TeamMembersFilters } from '../../utils/teamMembersFilters'
import {
  areAllProductLevelsUniform,
  buildPerChannelGrants,
} from '../../utils/productAccessLevels'

import styles from './TeamMembersTable.module.css'

const COLUMN_COUNT = 6
const SKELETON_ROWS = 3

const hasCustomizedPermissions = (
  member: TeamMember,
  assignableChannelIds: string[] | null,
): boolean => {
  if (!assignableChannelIds) return false
  const grants = buildPerChannelGrants(
    member.channelPermissions,
    assignableChannelIds,
  )
  return !areAllProductLevelsUniform(Array.from(grants.values()))
}

type StatusTone = 'success' | 'warning' | 'critical'

const StatusCell = ({
  tone,
  children,
}: {
  tone: StatusTone
  children: string
}): JSX.Element => (
  <Flex align="center" gap="xs">
    <span className={styles.dot} data-tone={tone} aria-hidden />
    <Text>{children}</Text>
  </Flex>
)

const Placeholder = (): JSX.Element => (
  <Text className={styles.placeholder} aria-label="Not applicable">
    —
  </Text>
)

type MemberRowProps = {
  member: TeamMember
  isCurrentUser: boolean
  isCustomized: boolean
  canManage: boolean
  onEdit: (member: TeamMember) => void
  onRemove: (member: TeamMember) => void
}

const MemberRow = ({
  member,
  isCurrentUser,
  isCustomized,
  canManage,
  onEdit,
  onRemove,
}: MemberRowProps): JSX.Element => {
  const displayName = getDisplayName(member)
  const assignedChannelCount = new Set(
    member.channelPermissions.map(({ channel }) => channel.id),
  ).size
  const hasTwoFactor = Boolean(member.account?.tfa)

  return (
    <Table.Row>
      <Table.Cell>
        <Flex align="center" gap="sm">
          <span className={styles.avatar}>
            <Avatar
              size="small"
              src={member.account?.avatar}
              alt=""
              fallback="letter"
              fallbackName={displayName}
            />
          </span>
          <Flex direction="column" className={styles.identity}>
            <Flex align="center" gap="xs" className={styles.nameRow}>
              <Heading
                as="p"
                size="xs"
                className={styles.name}
                title={displayName}
              >
                {displayName}
              </Heading>
              {member.isOwner && <Badge size="small">Owner</Badge>}
              {isCurrentUser && (
                <Badge size="small" variant="brand-subtle">
                  You
                </Badge>
              )}
            </Flex>
            <Text color="subtle" className={styles.email}>
              {member.email}
            </Text>
          </Flex>
        </Flex>
      </Table.Cell>
      <Table.Cell>
        <Flex direction="column">
          <Text>{COLLABORATION_ROLE_LABELS[member.collaborationRole]}</Text>
          {isCustomized && (
            <Text size="sm" color="subtle">
              Customized
            </Text>
          )}
        </Flex>
      </Table.Cell>
      <Table.Cell>
        {hasTwoFactor ? (
          <CheckIcon color="success" aria-label="2FA enabled" />
        ) : (
          <XIcon color="critical" aria-label="2FA not set up" />
        )}
      </Table.Cell>
      <Table.Cell>{assignedChannelCount}</Table.Cell>
      <Table.Cell>
        {member.isOwner ? (
          <Placeholder />
        ) : member.isLocked ? (
          <StatusCell tone="warning">Access suspended</StatusCell>
        ) : (
          <StatusCell tone="success">Active</StatusCell>
        )}
      </Table.Cell>
      <Table.Cell align="right" className={styles.actionsCell}>
        {canManage && !member.isOwner && (
          <DropdownMenu>
            <DropdownMenu.Trigger>
              <IconButton
                variant="tertiary"
                label={`Options for ${displayName}`}
              >
                <EllipsisVerticalIcon />
              </IconButton>
            </DropdownMenu.Trigger>
            <DropdownMenu.Content align="end">
              <DropdownMenu.Item
                disabled={member.isLocked}
                onSelect={(): void => onEdit(member)}
              >
                <PencilIcon /> Edit permissions
              </DropdownMenu.Item>
              <DropdownMenu.Item
                variant="critical"
                onSelect={(): void => onRemove(member)}
              >
                <TrashIcon /> Remove from team
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu>
        )}
      </Table.Cell>
    </Table.Row>
  )
}

type InvitationRowProps = {
  invitation: PendingInvitation
  canManage: boolean
  onResend: (invitation: PendingInvitation) => void
  onRevoke: (invitation: PendingInvitation) => void
}

const InvitationRow = ({
  invitation,
  canManage,
  onResend,
  onRevoke,
}: InvitationRowProps): JSX.Element => {
  const displayName = invitation.name || invitation.email
  const isExpired = new Date(invitation.expiresAt).getTime() < Date.now()
  // @todo Save the change once invitations can have their role updated.
  const [role, setRole] = useState<CollaborationRole>(
    invitation.collaborationRole,
  )

  const handleCopyLink = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(invitation.inviteLink)
      toast.success('Invite link copied')
    } catch {
      toast.error('Could not copy the invite link')
    }
  }

  return (
    <Table.Row>
      <Table.Cell>
        <Flex align="center" gap="sm">
          <span className={styles.avatar}>
            <Avatar
              size="small"
              alt=""
              fallback="letter"
              fallbackName={displayName}
            />
          </span>
          <Flex direction="column" className={styles.identity}>
            <Heading
              as="p"
              size="xs"
              className={styles.name}
              title={displayName}
            >
              {displayName}
            </Heading>
            <Text color="subtle" className={styles.email}>
              {invitation.email}
            </Text>
          </Flex>
        </Flex>
      </Table.Cell>
      <Table.Cell>
        <Select
          value={role}
          onValueChange={(value): void => setRole(value as CollaborationRole)}
        >
          <Select.Trigger
            size="medium"
            aria-label={`Role for ${displayName}`}
          />
          <Select.Content>
            {COLLABORATION_ROLES.map((value) => (
              <Select.Item key={value} value={value}>
                {COLLABORATION_ROLE_LABELS[value]}
              </Select.Item>
            ))}
          </Select.Content>
        </Select>
      </Table.Cell>
      <Table.Cell>
        <Placeholder />
      </Table.Cell>
      <Table.Cell>{invitation.channelPermissions.length}</Table.Cell>
      <Table.Cell>
        {isExpired ? (
          <StatusCell tone="critical">Invite expired</StatusCell>
        ) : (
          <StatusCell tone="warning">Invite sent</StatusCell>
        )}
      </Table.Cell>
      <Table.Cell align="right" className={styles.actionsCell}>
        {canManage && (
          <DropdownMenu>
            <DropdownMenu.Trigger>
              <IconButton
                variant="tertiary"
                label={`Options for ${displayName}`}
              >
                <EllipsisVerticalIcon />
              </IconButton>
            </DropdownMenu.Trigger>
            <DropdownMenu.Content align="end">
              <DropdownMenu.Item onSelect={(): void => void handleCopyLink()}>
                <LinkIcon /> Copy invite link
              </DropdownMenu.Item>
              <DropdownMenu.Item onSelect={(): void => onResend(invitation)}>
                <SendHorizontalIcon /> Resend invitation
              </DropdownMenu.Item>
              <DropdownMenu.Item
                variant="critical"
                onSelect={(): void => onRevoke(invitation)}
              >
                <XIcon /> Revoke invitation
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu>
        )}
      </Table.Cell>
    </Table.Row>
  )
}

const SkeletonRows = (): JSX.Element => (
  <>
    {Array.from({ length: SKELETON_ROWS }, (_row, index) => (
      <Table.Row key={index} data-testid="member-skeleton">
        <Table.Cell>
          <Flex align="center" gap="sm">
            <Skeleton rounded width={32} height={32} />
            <Flex direction="column" gap="2xs">
              <Skeleton width={140} height={16} />
              <Skeleton width={200} height={14} />
            </Flex>
          </Flex>
        </Table.Cell>
        {Array.from({ length: COLUMN_COUNT - 1 }, (_column, cell) => (
          <Table.Cell key={cell}>
            <Skeleton width={48} height={16} />
          </Table.Cell>
        ))}
      </Table.Row>
    ))}
  </>
)

type TeamMembersTableProps = {
  /** The page's members query, already filtered by `search` and `filters`. */
  members: ReturnType<typeof useTeamMembers>
  search?: string
  /** Decides which groups show; the server has already applied the rest. */
  filters?: TeamMembersFilters
  onClearFilters?: () => void
  onEdit: (member: TeamMember) => void
  onRemove: (member: TeamMember) => void
}

export const TeamMembersTable = ({
  members: membersQuery,
  search = '',
  filters = EMPTY_TEAM_MEMBERS_FILTERS,
  onClearFilters,
  onEdit,
  onRemove,
}: TeamMembersTableProps): JSX.Element => {
  const organizationId = useOrganizationId()
  const accountId = useAccountId()
  const canManage = useHasOrganizationAction('manageTeamMembers')
  const membersTotal = useOrganizationMembersTotal()

  const {
    teamMembers,
    loading,
    loadingMore,
    error,
    errorCode,
    hasNextPage,
    loadMore,
  } = membersQuery
  const { pendingInvitations, loading: invitationsLoading } =
    usePendingInvitations({
      organizationId: organizationId ?? '',
    })
  const { assignableChannels } = useAssignableChannels({
    organizationId: organizationId ?? '',
  })
  const assignableChannelIds = useMemo(
    () => assignableChannels?.map(({ channel }) => channel.id) ?? null,
    [assignableChannels],
  )
  const { revokeInvitation } = useRevokeInvitation()
  const { resendInvitation } = useResendInvitation()

  const handleResend = async (invitation: PendingInvitation): Promise<void> => {
    if (!organizationId) return
    const result = await resendInvitation({
      organizationId,
      invitationId: invitation.id,
    })
    if (result.success) {
      toast.success(`Invitation to ${invitation.email} resent`)
    } else {
      toast.error(result.error ?? 'Failed to resend invitation')
    }
  }

  const handleRevoke = async (invitation: PendingInvitation): Promise<void> => {
    if (!organizationId) return
    const result = await revokeInvitation({
      organizationId,
      invitationId: invitation.id,
    })
    if (result.success) {
      toast.success(`Invitation to ${invitation.email} revoked`)
    } else {
      toast.error(result.error ?? 'Failed to revoke invitation')
    }
  }

  if (error) {
    if (errorCode === 'FORBIDDEN') {
      return (
        <EmptyState variant="neutral">
          <EmptyState.Icon>
            <LockIcon />
          </EmptyState.Icon>
          <EmptyState.Heading>
            You don&apos;t have permission to view team members
          </EmptyState.Heading>
          <EmptyState.Description>
            Only administrators can manage the team. If you just joined, you may
            need to verify your email address first.
          </EmptyState.Description>
        </EmptyState>
      )
    }
    return (
      <EmptyState variant="critical">
        <EmptyState.Icon>
          <CriticalIcon />
        </EmptyState.Icon>
        <EmptyState.Heading>Failed to load team members</EmptyState.Heading>
        <EmptyState.Description>
          Something went wrong. Please try again later.
        </EmptyState.Description>
      </EmptyState>
    )
  }

  // Pending shows only invitations; the members query is skipped.
  const isPendingOnly = filters.status === 'pending'
  const isLoading =
    !organizationId ||
    (isPendingOnly
      ? invitationsLoading && pendingInvitations.length === 0
      : loading && !teamMembers)
  const members = isPendingOnly ? [] : (teamMembers ?? [])
  const memberCount = membersTotal ?? members.length
  const isSearching = search.trim() !== ''
  const isFiltering = hasActiveTeamMembersFilters(filters)

  // Invites hidden while searching or on any filter other than Pending
  const showPending =
    pendingInvitations.length > 0 &&
    !isSearching &&
    canShowPendingInvitations(filters)

  if (!isLoading && isFiltering && members.length === 0 && !showPending) {
    return (
      <EmptyState variant="neutral" size="xlarge">
        <EmptyState.Icon>
          <ListFilterIcon />
        </EmptyState.Icon>
        <EmptyState.Heading>No members match these filters</EmptyState.Heading>
        {onClearFilters && (
          <EmptyState.Actions>
            <Button variant="secondary" onClick={onClearFilters}>
              Clear Filters
            </Button>
          </EmptyState.Actions>
        )}
      </EmptyState>
    )
  }

  // No results empty state
  if (!isLoading && isSearching && members.length === 0) {
    return (
      <EmptyState variant="neutral" size="xlarge">
        <EmptyState.Icon>
          <SearchIcon />
        </EmptyState.Icon>
        <EmptyState.Heading>No members found</EmptyState.Heading>
        <EmptyState.Description>
          Try adjusting your search terms.
        </EmptyState.Description>
      </EmptyState>
    )
  }

  if (!isLoading && members.length === 0 && pendingInvitations.length === 0) {
    return (
      <EmptyState variant="neutral" size="xlarge">
        <EmptyState.Icon>
          <UserIcon />
        </EmptyState.Icon>
        <VisuallyHidden>
          <EmptyState.Heading>No team members</EmptyState.Heading>
        </VisuallyHidden>
        <EmptyState.Description>
          Invite team members to collaborate on your social media content.
        </EmptyState.Description>
      </EmptyState>
    )
  }

  return (
    <Table className={styles.table}>
      <VisuallyHidden as="caption">Team members</VisuallyHidden>
      <colgroup>
        <col />
        <col className={styles.roleColumn} />
        <col className={styles.tfaColumn} />
        <col className={styles.channelsColumn} />
        <col className={styles.statusColumn} />
        <col className={styles.actionsColumn} />
      </colgroup>
      <Table.Header>
        <Table.Row>
          <Table.HeaderCell scope="col">
            Members · {memberCount}
          </Table.HeaderCell>
          <Table.HeaderCell scope="col">Role</Table.HeaderCell>
          <Table.HeaderCell scope="col">2FA</Table.HeaderCell>
          <Table.HeaderCell scope="col">Channels</Table.HeaderCell>
          <Table.HeaderCell scope="col">Status</Table.HeaderCell>
          <Table.HeaderCell
            scope="col"
            align="right"
            className={styles.actionsCell}
          >
            {/* @todo Column picker; disabled until there is something to pick. */}
            <IconButton variant="tertiary" label="Customize columns" disabled>
              <Columns3Icon />
            </IconButton>
          </Table.HeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {isLoading ? (
          <SkeletonRows />
        ) : (
          <>
            {members.map((member) => (
              <MemberRow
                key={member.id}
                member={member}
                isCurrentUser={member.id === accountId}
                isCustomized={hasCustomizedPermissions(
                  member,
                  assignableChannelIds,
                )}
                canManage={canManage}
                onEdit={onEdit}
                onRemove={onRemove}
              />
            ))}
            {hasNextPage && (
              <Table.Row>
                <Table.Cell colSpan={COLUMN_COUNT} align="center">
                  <Button
                    variant="tertiary"
                    size="small"
                    loading={loadingMore}
                    disabled={loadingMore}
                    onClick={loadMore}
                  >
                    Load more members
                  </Button>
                </Table.Cell>
              </Table.Row>
            )}
            {showPending && (
              <>
                <Table.Row>
                  <Table.Cell
                    colSpan={COLUMN_COUNT}
                    className={styles.groupCell}
                  >
                    Pending Invite · {pendingInvitations.length}
                  </Table.Cell>
                </Table.Row>
                {pendingInvitations.map((invitation) => (
                  <InvitationRow
                    key={invitation.id}
                    invitation={invitation}
                    canManage={canManage}
                    onResend={(inv): void => void handleResend(inv)}
                    onRevoke={(inv): void => void handleRevoke(inv)}
                  />
                ))}
              </>
            )}
          </>
        )}
      </Table.Body>
    </Table>
  )
}
